import * as React from 'react';
import { css, StyleSheet } from 'aphrodite';
import { Colors, MediaBreakPointUp } from '@lean-body/components/styles';
import { CloseIcon } from '../../atoms/icons/close_icon';
import { SearchIcon } from '../../atoms/icons/search_icon';
import { searchDurationClockMinutes } from '../../atoms/icons/clock_icon';
import { Overlay } from '../overlay';
import { circleLabel } from './circle_label';
import { SearchFilterCircles } from './search_filter_circles';
import { SearchFilterDifficulty } from './search_filter_difficulty';
import { SearchFilterDuration } from './search_filter_duration';
import { SearchFilterInstructors } from './search_filter_instructors';
import { SearchFilterProgram } from './search_filter_program';
import { SEARCH_FILTERS } from './search_filter_state';
export const SearchFilterDialog = ({ config, active, setActive, draft, styles: lessonStyles, durations, difficulties, partTags, trainers, onToggle, onSelectCategory, onClose, onSubmit, submitDisabled, }) => {
    const styleItems = lessonStyles.map((style) => ({
        id: style.id,
        name: circleLabel(style.name),
        imageUrl: style.thumbnailURL(config),
        selected: draft.styleIDs.includes(style.id),
    }));
    const tagItems = partTags.map((tag) => ({
        id: tag.id,
        name: tag.name,
        imageUrl: tag.thumbnailURL(config),
        selected: draft.tagIDs.includes(tag.id),
    }));
    const trainerItems = trainers.map((trainer) => ({
        id: trainer.id,
        name: trainer.name,
        imageUrl: trainer.thumbnailURL,
        selected: draft.trainerIDs.includes(trainer.id),
    }));
    const durationItems = durations.map(({ name, value }, index) => ({
        value,
        name,
        minute: searchDurationClockMinutes[index],
        selected: draft.durationIDs.includes(value),
    }));
    const difficultyItems = difficulties.map((difficulty) => ({
        id: difficulty.id,
        name: difficulty.name,
        degree: difficulty.degree,
        selected: draft.difficultyIDs.includes(difficulty.id),
    }));
    const submit = () => {
        if (submitDisabled)
            return;
        onSubmit();
    };
    return (React.createElement(Overlay, { background: Colors.BackgroundDialog },
        React.createElement("div", { className: css(styles.overlay), onClick: onClose },
            React.createElement("div", { className: css(styles.dialog), onClick: (e) => e.stopPropagation() },
                React.createElement("div", { className: css(styles.dialogHeader) },
                    React.createElement("div", { className: css(styles.close), onClick: onClose, "aria-label": "\u9589\u3058\u308B" },
                        React.createElement(CloseIcon, { color: Colors.Black, size: 16 }))),
                React.createElement("div", { className: css(styles.tabs) }, SEARCH_FILTERS.map(({ key, label }) => (React.createElement("div", { key: key, className: css(styles.tab, active === key && styles.tabActive), onClick: () => setActive(key) }, label)))),
                React.createElement("div", { className: css(styles.dialogBody) },
                    active === 'style' && React.createElement(SearchFilterCircles, { items: styleItems, onClick: (id) => onToggle('style', id) }),
                    active === 'tag' && React.createElement(SearchFilterCircles, { items: tagItems, onClick: (id) => onToggle('tag', id) }),
                    active === 'trainer' && (React.createElement(SearchFilterInstructors, { items: trainerItems, onClick: (id) => onToggle('trainer', id) })),
                    active === 'program' && React.createElement(SearchFilterProgram, { category: draft.category, onSelect: onSelectCategory }),
                    active === 'duration' && (React.createElement(SearchFilterDuration, { items: durationItems, onClick: (id) => onToggle('duration', id) })),
                    active === 'difficulty' && (React.createElement(SearchFilterDifficulty, { items: difficultyItems, onClick: (id) => onToggle('difficulty', id) }))),
                React.createElement("div", { className: css(styles.submitBar) },
                    React.createElement("div", { className: css(styles.submit, submitDisabled && styles.submitDisabled), onClick: submit },
                        React.createElement(SearchIcon, { color: Colors.White, classObject: styles.submitIcon }),
                        "\u691C\u7D22"))))));
};
const instructorRowHeight = 98;
const instructorRowGap = 16;
const dialogBodyVerticalPadding = 32;
const spInstructorRows = instructorRowHeight * 3;
const spInstructorGaps = instructorRowGap * 2;
const mdInstructorRows = instructorRowHeight * 5;
const mdInstructorGaps = instructorRowGap * 4;
const spDialogBodyHeight = spInstructorRows + spInstructorGaps + dialogBodyVerticalPadding;
const mdDialogBodyHeight = mdInstructorRows + mdInstructorGaps + dialogBodyVerticalPadding;
const styles = StyleSheet.create({
    overlay: {
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        [MediaBreakPointUp.MD]: {
            alignItems: 'center',
        },
    },
    dialog: {
        width: '100%',
        maxHeight: '75vh',
        background: Colors.White,
        borderRadius: '14px 14px 0 0',
        boxShadow: '0 0 15px rgba(0, 180, 199, 0.08)',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        [MediaBreakPointUp.MD]: {
            width: 484,
            maxHeight: '80vh',
            borderRadius: 14,
            boxShadow: '0 0 30px rgba(0, 180, 199, 0.08)',
        },
    },
    dialogHeader: {
        display: 'flex',
        justifyContent: 'flex-end',
        flexShrink: 0,
        padding: '8px 20px 0',
    },
    close: {
        width: 30,
        height: 30,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
    },
    tabs: {
        display: 'flex',
        gap: 12,
        overflowX: 'auto',
        scrollbarWidth: 'none',
        '::-webkit-scrollbar': {
            width: 0,
            height: 0,
        },
        flexShrink: 0,
        marginTop: 8,
        paddingLeft: 20,
        paddingRight: 20,
        borderBottom: `1px solid ${Colors.BorderLightGray}`,
    },
    tab: {
        padding: '8px 4px',
        fontSize: 13,
        color: Colors.GrayDarkest,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
    },
    tabActive: {
        color: Colors.Black,
        fontWeight: 'bold',
        borderBottom: `2px solid ${Colors.PrimaryDark}`,
    },
    dialogBody: {
        overflow: 'auto',
        padding: '16px 20px',
        // インストラクター1行98px・行間16px。SPは3行、タブレット以上は5行が見える高さ
        height: spDialogBodyHeight,
        flexShrink: 1,
        minHeight: 0,
        [MediaBreakPointUp.MD]: {
            height: mdDialogBodyHeight,
        },
    },
    submitBar: {
        flexShrink: 0,
        background: Colors.White,
        boxShadow: '0 0 15px rgba(0, 0, 0, 0.16)',
        borderRadius: '14px 14px 0 0',
        padding: 16,
    },
    submit: {
        width: '100%',
        height: 48,
        borderRadius: 24,
        background: Colors.PrimaryDark,
        color: Colors.White,
        fontSize: 16,
        fontWeight: 'bold',
        letterSpacing: '1.28px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
    },
    submitDisabled: {
        background: Colors.Gray,
        cursor: 'default',
    },
    submitIcon: {
        width: 16,
        height: 16,
    },
});
